import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
    LoginCountEntry,
    SubscriptionsPerDayEntry,
    UsersByTypeEntry,
    fetchLoginMetrics,
    fetchSubscriptionsPerDay,
    fetchUsersByType,
} from "../services/polarisService";
import { formatRoleLabel } from "../utils/roleLabels";
import "../styles/polaris-admin.css";

/* ---- helpers ---- */

function todayStr(): string {
    return new Date().toISOString().split("T")[0];
}

function daysAgoStr(n: number): string {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split("T")[0];
}

function shortDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}

/* ---- Proper Bar Chart ---- */

interface BarEntry {
    label: string;
    value: number;
    color?: string;
}

interface BarChartProps {
    entries: BarEntry[];
    height?: number;
    barColor?: string;
}

function BarChart({ entries, height = 160, barColor = "#2563eb" }: BarChartProps) {
    if (entries.length === 0) {
        return <div className="pa-chart-empty">No data for this period</div>;
    }

    const maxVal = Math.max(...entries.map((e) => e.value), 1);

    // Y-axis: 4 nice round ticks
    const rawStep = maxVal / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
    const step = Math.ceil(rawStep / magnitude) * magnitude || 1;
    const yTicks = Array.from({ length: 5 }, (_, i) => i * step);
    const chartMax = yTicks[yTicks.length - 1];

    return (
        <div className="pa-chart-container">
            {/* Y-axis labels */}
            <div className="pa-chart-y-axis">
                {[...yTicks].reverse().map((t) => (
                    <span key={t} className="pa-chart-y-label">{t.toLocaleString()}</span>
                ))}
            </div>

            {/* Chart area */}
            <div className="pa-chart-area">
                {/* Horizontal grid lines */}
                <div className="pa-chart-grid">
                    {yTicks.map((t) => (
                        <div
                            key={t}
                            className="pa-chart-grid-line"
                            style={{ bottom: `${(t / chartMax) * 100}%` }}
                        />
                    ))}
                </div>

                {/* Bars */}
                <div className="pa-chart-bars" style={{ height }}>
                    {entries.map((e, i) => (
                        <div key={i} className="pa-chart-bar-col" title={`${e.label}: ${e.value.toLocaleString()}`}>
                            <div className="pa-chart-bar-value">{e.value > 0 ? e.value.toLocaleString() : ""}</div>
                            <div
                                className="pa-chart-bar"
                                style={{
                                    height: `${Math.max((e.value / chartMax) * 100, e.value > 0 ? 2 : 0)}%`,
                                    background: e.color ?? barColor,
                                }}
                            />
                        </div>
                    ))}
                </div>

                {/* X-axis labels */}
                <div className="pa-chart-x-axis">
                    {entries.map((e, i) => (
                        <div key={i} className="pa-chart-x-label">{e.label}</div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---- Login Metrics Panel ---- */

const PLAN_COLORS = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2"];

function LoginMetricsPanel() {
    const [from, setFrom] = useState(daysAgoStr(30));
    const [to, setTo] = useState(todayStr());
    const [entries, setEntries] = useState<LoginCountEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchLoginMetrics(from, to);
            setEntries(data.entries);
        } catch (e: any) {
            setError(e.message ?? "Failed to load login metrics");
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    useEffect(() => { load(); }, [load]);

    const totalLogins = useMemo(() => entries.reduce((s, e) => s + e.count, 0), [entries]);
    const peakDay = useMemo(() => entries.reduce((best, e) => e.count > best.count ? e : best, { date: "—", count: 0 }), [entries]);
    const avgLogins = useMemo(() => entries.length > 0 ? Math.round(totalLogins / entries.length) : 0, [totalLogins, entries]);

    const barEntries = useMemo(() =>
        entries.map((e) => ({ label: shortDate(e.date), value: e.count })),
        [entries]
    );

    return (
        <div className="pa-metric-card pa-metric-card-full">
            <div className="pa-metric-card-title">Daily Logins</div>

            <div className="pa-date-filter">
                <label htmlFor="login-from">From</label>
                <input id="login-from" type="date" className="pa-date-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                <label htmlFor="login-to">To</label>
                <input id="login-to" type="date" className="pa-date-input" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} />
            </div>

            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading login data…</div>}

            {!loading && !error && (
                <>
                    <div className="pa-login-stats">
                        <div className="pa-login-stat">
                            <span className="pa-login-stat-value">{totalLogins.toLocaleString()}</span>
                            <span className="pa-login-stat-label">Total logins</span>
                        </div>
                        <div className="pa-login-stat">
                            <span className="pa-login-stat-value">{peakDay.count.toLocaleString()}</span>
                            <span className="pa-login-stat-label">Peak day{peakDay.date !== "—" ? ` (${shortDate(peakDay.date)})` : ""}</span>
                        </div>
                        <div className="pa-login-stat">
                            <span className="pa-login-stat-value">{avgLogins.toLocaleString()}</span>
                            <span className="pa-login-stat-label">Daily average</span>
                        </div>
                    </div>
                    <BarChart entries={barEntries} height={160} />
                </>
            )}
        </div>
    );
}

/* ---- Users by Type Panel ---- */

function UsersByTypePanel() {
    const [entries, setEntries] = useState<UsersByTypeEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchUsersByType()
            .then((d) => setEntries(d.entries))
            .catch((e) => setError(e.message ?? "Failed to load user type data"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="pa-metric-card pa-metric-card-full">
            <div className="pa-metric-card-title">Users by Type</div>
            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading…</div>}
            {!loading && !error && (
                <div className="pa-type-row">
                    {entries.length === 0 ? (
                        <div style={{ color: "var(--muted)", fontSize: 13 }}>No data</div>
                    ) : (
                        entries.map((e, i) => (
                            <div key={e.profileType} className="pa-type-card">
                                <div className="pa-type-count" style={{ color: PLAN_COLORS[i % PLAN_COLORS.length] }}>
                                    {e.count.toLocaleString()}
                                </div>
                                <div className="pa-type-label">{formatRoleLabel(e.profileType)}</div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

/* ---- Subscriptions per Day Panel ---- */

function SubscriptionsPerDayPanel() {
    const [entries, setEntries] = useState<SubscriptionsPerDayEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchSubscriptionsPerDay()
            .then((d) => setEntries(d.entries))
            .catch((e) => setError(e.message ?? "Failed to load subscription data"))
            .finally(() => setLoading(false));
    }, []);

    const plans = useMemo(() => [...new Set(entries.map((e) => e.planName))].sort(), [entries]);
    const dates = useMemo(() => [...new Set(entries.map((e) => e.date))].sort(), [entries]);

    // Overall totals per date (all plans combined)
    const overallEntries = useMemo(() =>
        dates.map((date) => ({
            label: shortDate(date),
            value: entries.filter((e) => e.date === date).reduce((s, e) => s + e.count, 0),
        })),
        [dates, entries]
    );

    // Per-plan entries
    const perPlanEntries = useMemo(() =>
        plans.map((plan, pi) => ({
            plan,
            color: PLAN_COLORS[pi % PLAN_COLORS.length],
            bars: dates.map((date) => ({
                label: shortDate(date),
                value: entries.find((e) => e.date === date && e.planName === plan)?.count ?? 0,
            })),
        })),
        [plans, dates, entries]
    );

    return (
        <div className="pa-metric-card pa-metric-card-full">
            <div className="pa-metric-card-title">Subscriptions per Day</div>
            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading…</div>}
            {!loading && !error && entries.length === 0 && (
                <div className="pa-chart-empty">No subscription data available</div>
            )}
            {!loading && !error && entries.length > 0 && (
                <div className="pa-sub-charts">
                    {/* Overall chart */}
                    <div className="pa-sub-chart-block">
                        <div className="pa-sub-chart-label">All Plans</div>
                        <BarChart entries={overallEntries} height={120} barColor="#2563eb" />
                    </div>

                    {/* One chart per plan */}
                    {perPlanEntries.map(({ plan, color, bars }) => (
                        <div key={plan} className="pa-sub-chart-block">
                            <div className="pa-sub-chart-label" style={{ color }}>
                                <span className="pa-sub-chart-dot" style={{ background: color }} />
                                {plan}
                            </div>
                            <BarChart entries={bars} height={120} barColor={color} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   Metrics Page
   ============================================================ */

export function PolarisMetricsOverviewPanel() {
    return (
        <div className="pa-metrics-stack">
            <LoginMetricsPanel />
            <UsersByTypePanel />
            <SubscriptionsPerDayPanel />
        </div>
    );
}

export default function PolarisMetricsPage() {
    const location = useLocation();
    const onCourseInsights = location.pathname.includes("/metrics/course-insights");

    return (
        <section className="pa-section" data-testid="polaris-metrics-page">
            <h1 className="pa-page-title">Metrics</h1>

            <div className="pa-tabs" role="tablist" aria-label="Metrics sections">
                <NavLink
                    to="/polaris-admin/metrics"
                    end
                    role="tab"
                    aria-selected={!onCourseInsights}
                    className={({ isActive }) => `pa-tab${isActive ? " active" : ""}`}
                    data-testid="polaris-metrics-tab-overview"
                >
                    Overview
                </NavLink>
                <NavLink
                    to="/polaris-admin/metrics/course-insights"
                    role="tab"
                    aria-selected={onCourseInsights}
                    className={({ isActive }) => `pa-tab${isActive ? " active" : ""}`}
                    data-testid="polaris-metrics-tab-course-insights"
                >
                    Course Insights
                </NavLink>
            </div>

            <Outlet />
        </section>
    );
}
